/**
 * Hook: useAssembleiaVencimentos
 * Busca as datas de vencimento de assembleia dos grupos ativos, sem repetição.
 * Quando há período de produção, restringe às datas dentro desse intervalo.
 */

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { getProductionPeriod } from '@/utils/productionPeriod';

type ProductionPeriod = { month: number; year: number };

export function useAssembleiaVencimentos(
  enabled: boolean = true,
  productionPeriod?: ProductionPeriod | null,
  startDay: number = 1
) {
  const periodKey = productionPeriod
    ? `${productionPeriod.year}-${productionPeriod.month}-${startDay}`
    : 'all';

  return useQuery({
    queryKey: ['assembleia-vencimentos', periodKey],
    enabled,
    queryFn: async () => {
      let query = supabase
        .from('grupos')
        .select('data_vencimento_grupo')
        .eq('ativo', true)
        .not('data_vencimento_grupo', 'is', null)
        .order('data_vencimento_grupo', { ascending: true });

      if (productionPeriod) {
        const { startDate, endDate } = getProductionPeriod(
          productionPeriod.month,
          productionPeriod.year,
          startDay
        );
        query = query
          .gte('data_vencimento_grupo', format(startDate, 'yyyy-MM-dd'))
          .lte('data_vencimento_grupo', format(endDate, 'yyyy-MM-dd'));
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Erro ao buscar vencimentos de assembleia: ${error.message}`);
      }

      const datas = (data ?? [])
        .map((grupo) => grupo.data_vencimento_grupo)
        .filter((data): data is string => Boolean(data));

      return [...new Set(datas)];
    },
    staleTime: 1000 * 60 * 10, // 10 minutos
    retry: 2,
  });
}
